/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import styled from 'styled-components';

import { Alert, Box, CircularProgress, InputAdornment } from '@mui/material';
import { useTheme } from '../../../styles';
import { AutocompleteContainer, AutocompleteInput, AutocompletePopper } from '../Base';
import { Command } from '@store/slices';
import { TryOutlined } from '@mui/icons-material';
import { MessagingProfileListItem } from '../ListItem/MessagingProfileListItem';
import { MessagingProfileConfigure } from './MessagingProfileConfigure';
import { TelnyxMessagingProfile } from '@requests/telnyx';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  & .MuiInputBase-adornedStart {
    padding: 0 9px !important;
  }
`;

interface Props {
  value: string;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => void;
  disabled?: boolean;
  loading?: boolean;
  labels: {
    id: string;
    label: string;
    value: string;
    showId?: boolean;
    metadata: TelnyxMessagingProfile;
  }[];
  placeholder?: string;
  command: Command;
  triggerProfile?: boolean;
}

export const MessagingProfiles: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  labels,
  placeholder,
  loading,
  triggerProfile = true,
}) => {
  const autocompleteRef = useRef(null);
  const theme = useTheme();

  const [open, setOpen] = useState<boolean>(false);

  const selectedApp = labels.find((item) => item.value === value);

  const isNotV2 = selectedApp && selectedApp?.metadata?.webhook_api_version !== '2';
  const isNotV2Beta = selectedApp && selectedApp?.metadata?.webhook_api_v2_beta === false;
  const isProfileError = isNotV2 || isNotV2Beta;

  const profilePortalLink = (
    <a
      style={{ color: theme.primary }}
      href={
        window.location.origin.includes('dev')
          ? `https://portaldev.telnyx.com/#/programmable-messaging/profiles/${value}?tab=details`
          : `https://portal.telnyx.com/#/programmable-messaging/profiles/${value}?tab=details`
      }
      target='_blank'
      rel='noreferrer'
    >
      here
    </a>
  );

  return (
    <Container>
      <AutocompleteContainer open={open}>
        <Autocomplete
          value={labels.find((item) => item?.value === value) || null}
          ref={autocompleteRef}
          onChange={(event, newValue) => {
            onChange(event, newValue || { value: '', label: '' });
            setOpen(false);

            if (newValue) (document?.activeElement as HTMLElement)?.blur();
          }}
          disabled={disabled}
          PopperComponent={AutocompletePopper}
          renderOption={(props, option) => <MessagingProfileListItem {...props} option={option} />}
          autoHighlight
          options={labels}
          disablePortal
          getOptionLabel={(option) => option?.label || ''}
          renderInput={(params) => (
            <AutocompleteInput
              open={open}
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              endAdornment={loading ? <CircularProgress sx={{ color: theme.primary }} size={14} /> : null}
              startAdornment={
                value &&
                !loading && (
                  <InputAdornment position='start'>
                    <Box
                      component='span'
                      sx={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        borderRadius: '3px',
                        mr: 0.35,
                        ml: 0.31,
                        mt: '0px',
                      }}
                    >
                      <TryOutlined sx={{ fontSize: '20px', color: theme.primary }} />
                    </Box>
                  </InputAdornment>
                )
              }
            />
          )}
        />
      </AutocompleteContainer>
      {!isProfileError && selectedApp && triggerProfile && <MessagingProfileConfigure profile={selectedApp.metadata} />}
      {isNotV2 && triggerProfile && (
        <Alert severity='error'>
          Flow does not support v1 messaging profiles. Please choose a v2 profile (with v2 beta enabled), or update this
          profile from your portal account {profilePortalLink}.
        </Alert>
      )}
      {!isNotV2 && isNotV2Beta && triggerProfile && (
        <Alert severity='error'>
          Flow currently only supports messaging profiles with the v2 BETA flag enabled. You can enable this feature
          from your portal account {profilePortalLink}.
        </Alert>
      )}
    </Container>
  );
};
